/**
 * Functions that helps the time
 *
 * @packageDocumentation
 */

/* eslint-disable no-magic-numbers */
/**
 * Key value pair of times in seconds
 * Assumption: nothing older than 1 year
 * @enum
 */
enum Epochs {
    year = 31536000,
    month = 2592000,
    day = 86400,
    hour = 3600,
    minute = 60,
    second = 1
}
type EpochsStrings = keyof typeof Epochs
/* eslint-enable no-magic-numbers */

interface IDuration {
    interval: number
    epoch: string
}

/**
 * Get matching epoch for the given seconds
 * @param seconds
 * @return {IDuration} number of which epochs as object
 */
const getDuration = (timeAgoInSeconds: number): IDuration => {
    const result = { epoch: 'second', interval: 1 } // default return
    // loop through epochs to find a match
    for (const name of Object.keys(Epochs) as EpochsStrings[]) {
        const interval: number = Math.floor(timeAgoInSeconds / Epochs[name])
        if (interval >= 1) {
            // match break loop
            // object-literal-shorthand breaks compiling
            return {
                epoch: name,
                interval: interval
            }
        }
    }
    return result
}

/**
 * Convert duration in seconds into ISO 8601 Duration
 * @param {number} duration
 * @return {string}
 */
export const secondsToDurationISO = (duration: number): string => {
    const hours = Math.floor(duration / Epochs.hour)
    const hrStr = hours ? `${hours}H` : ''
    const minutes = Math.floor((duration % Epochs.hour) / Epochs.minute)
    const minStr = minutes ? `${minutes}M` : ''
    const seconds = (duration % Epochs.hour) % Epochs.minute
    const secStr = seconds ? `${seconds}S` : ''
    return `PT${hrStr}${minStr}${secStr}`
}

/**
 * Convert epoch timestamp into the desired UI display
 * @param {number} timestamp
 * @return {string}
 */
export const epochToISO = (timestamp: number): string => {
    const toMil = 1000
    const date = new Date(timestamp * toMil)
    return date.toISOString()
}

/**
 * Convert the given date string into the desired UI display
 * Date String => "{number} {timeval}{plural} ago"
 * @param {string} date
 * @return {string}
 */
export const renderUITimeAgo = (date: string): string => {
    const msDivision = 1000
    const timeAgoInSeconds: number = Math.floor(
        (Date.now() - new Date(date).getTime()) / msDivision
    )
    const { interval, epoch } = getDuration(timeAgoInSeconds)
    const suffix = interval === 1 ? '' : 's'

    return `${interval} ${epoch}${suffix} ago`
}

/**
 * Strip out values from the given duration format
 * For the desired UI display
 * PT#H#M#S | PT#M#S => HH:MM:SS | H:MM:SS | MM:SS | M:SS
 * Assumption: never longer than 24hrs
 * @param {string} (ISO 8601 duration)
 * @return {string}
 */
export const renderUIISODuration = (duration: string): string => {
    // https://developers.google.com/youtube/v3/docs/videos
    // contentDetails.duration
    const amountToPad = 2
    const replace = duration.replace(
        /PT((\d+)(H))?((\d+)(M))?((\d+)(S))/,
        '$2:$5:$8'
    )
    const split = replace.split(':').filter((val) => val.length !== 0)
    const padded: string[] = []
    split.forEach((item: string, index: number) => {
        // add leading 0 to time if not the first group
        if (index !== 0) {
            padded.push(item.padStart(amountToPad, '0'))
        } else {
            padded.push(item)
        }
    })
    return padded.join(':')
}
